(ns hai.home.events
  "Home page lifecycle: init/teardown fan-out and the minute tick."
  (:require
   [hai.home.calendar :as calendar]
   [hai.home.clock :as clock]
   [hai.mini-apps.data :as mini-apps]
   [hai.notes.recent :as recent]
   [hai.tasks.data :as tasks]
   [hai.ui.rf-sugar :refer [>e defeffect defevent-fx]]
   [hai.web-app.sync :as sync]))

;; ---------------------------------------------------------------------------
;; Minute tick
;; ---------------------------------------------------------------------------

(defonce ^:private tick-handle (atom nil))

(defn- schedule-tick! []
  (reset! tick-handle
          (js/setTimeout (fn []
                           (>e [:home/minute-tick])
                           (schedule-tick!))
                         (- 60000 (mod (.getTime (js/Date.)) 60000)))))

(defn- on-visibility-change []
  (when-not (.-hidden js/document)
    (>e [::clock/tick])))

(defeffect :home/start-clock [_]
  (schedule-tick!)
  (js/document.addEventListener "visibilitychange" on-visibility-change))

(defeffect :home/stop-clock [_]
  (js/document.removeEventListener "visibilitychange" on-visibility-change)
  (when-let [handle @tick-handle]
    (js/clearTimeout handle)
    (reset! tick-handle nil)))

(defevent-fx :home/minute-tick [_ _]
  {:fx [[:dispatch [::clock/tick]]
        [:dispatch [::calendar/tick]]]})

;; ---------------------------------------------------------------------------
;; Page lifecycle
;; ---------------------------------------------------------------------------

;; What each mini-app on Home needs loaded once the platform has said which
;; ones this user gets. Progress draws the calendar's meetings and the day's
;; tasks, so it asks for both; a load two cards share is dispatched once.
(def ^:private loads-by-tool
  {:notes    [[::recent/load]]
   :tasks    [[::tasks/load-owned-or-assigned-tasks]]
   :calendar [[::calendar/init]]
   :progress [[::calendar/init] [::tasks/load-owned-or-assigned-tasks]]})

;; What each mini-app refetches when another tab changes a scope it shows.
(def ^:private refetches-by-tool
  {:tasks    {:tasks [[::tasks/load-owned-or-assigned-tasks]]}
   :progress {:tasks [[::tasks/load-owned-or-assigned-tasks]]}})

(def ^:private watched-scopes
  (into #{} (mapcat keys) (vals refetches-by-tool)))

(defevent-fx ::refetch [{:keys [db]} {:keys [scope]}]
  {:fx (into []
             (comp (mapcat #(get-in refetches-by-tool [% scope]))
                   (distinct)
                   (map (fn [event] [:dispatch event])))
             (mini-apps/tools db))})

(defn- watch-fx
  [scope]
  [:dispatch [::sync/watch {:owner :home
                            :scope scope
                            :refetch [[::refetch {:scope scope}]]}]])

(defevent-fx :home/init [_ _]
  ;; The clock lives in the route lifecycle, not a view, so meeting statuses
  ;; and countdowns keep refreshing on both the desktop and mobile bodies.
  {:fx (into [[:home/start-clock true]
              [:dispatch [::clock/tick]]
              [:dispatch [::mini-apps/load {:on-success-fx [[:dispatch [::mini-apps-loaded]]]}]]]
             (map watch-fx)
             watched-scopes)})

(defevent-fx ::mini-apps-loaded [{:keys [db]} _]
  {:fx (into []
             (comp (mapcat loads-by-tool)
                   (distinct)
                   (map (fn [load] [:dispatch load])))
             (mini-apps/tools db))})

(defevent-fx :home/teardown [_ _]
  {:fx [[:dispatch [::sync/unwatch {:owner :home}]]
        [:home/stop-clock true]
        [:dispatch [::clock/clear]]
        [:dispatch [::calendar/teardown]]
        [:dispatch [::tasks/clear]]
        [:dispatch [::recent/clear]]
        [:dispatch [::mini-apps/clear]]]})
